/**
 * Shared layout chrome for every staff-facing page (Share & Mint,
 * Proposal Search, Contract Review, RFQs detail page).
 *
 * Why this exists:
 *   Before the redesign each staff page had its own deep-navy header
 *   + a single "← BACK TO SHARE" link, so the other staff features
 *   were invisible unless you already knew their URLs. Discoverability
 *   was effectively zero. Now every staff page wears the same warm-
 *   stone palette and the same persistent navigation strip, so the
 *   three core capabilities (mint a link, search the back-catalogue,
 *   review a client contract) are one click away from anywhere.
 *
 * Palette: stone-50 background, white cards, navy/cobalt accents -
 * matches the consulting-deck Excel export we just shipped, so the
 * web app and the artefacts it produces read as a single brand.
 */
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  FileDown,
  FileSearch,
  Mail,
  Sparkles,
  ArrowUpRight,
  LogOut,
  UserPlus,
  GitBranch,
  Activity,
  Users,
  Landmark,
  Megaphone,
} from "lucide-react";
import { PoundSterling, CalendarRange } from "lucide-react";
import { useAuth } from "../../lib/auth";

// Admin-only nav items (adminOnly: true) are hidden for non-admin
// staff; the matching routes are wrapped in <Staff adminOnly> and the
// backend endpoints require the admin role, so the nav filter is
// purely cosmetic.
const NAV_ITEMS = [
  {
    to: "/share",
    label: "Send",
    icon: Sparkles,
    match: (path) => path === "/share" || path.startsWith("/share/"),
    testid: "staff-nav-share",
    blurb: "Mint a permanent client link from any Cognito proposal",
  },
  {
    to: "/activity",
    label: "Track",
    icon: Activity,
    match: (path) => path.startsWith("/activity"),
    testid: "staff-nav-activity",
    blurb: "Hot/warm/cold engagement scoring + per-email audit trail for every shared proposal",
  },
  {
    to: "/pipeline",
    label: "Funnel",
    icon: GitBranch,
    match: (path) => path.startsWith("/pipeline"),
    testid: "staff-nav-pipeline",
    blurb: "Live funnel mirror of every Cognito saved view",
  },
  {
    to: "/sow/emails",
    label: "Emails",
    icon: Mail,
    match: (path) => path.startsWith("/sow"),
    testid: "staff-nav-emails",
    adminOnly: true,
    blurb: "Commissioning emails: wording, recipients and the alert list",
  },
  {
    to: "/revenue",
    label: "CFO Cockpit",
    icon: PoundSterling,
    match: (path) => path.startsWith("/revenue"),
    testid: "staff-nav-revenue",
    blurb: "Cash, margins, debtors and outlook - the full financial picture",
    adminOnly: true,
  },
  {
    to: "/quarter",
    label: "Quarterly",
    icon: CalendarRange,
    match: (path) => path.startsWith("/quarter"),
    testid: "staff-nav-quarter",
    blurb: "Quarter at a Glance - the all-hands update, auto-filled KPIs + gated team link",
    adminOnly: true,
  },
  {
    to: "/exports",
    label: "Exports",
    icon: FileDown,
    match: (path) => path.startsWith("/exports"),
    testid: "staff-nav-exports",
    blurb: "One-shot exports - Grids-only XLSX, FX-converted PDF, supplier RFQs",
  },
  {
    to: "/contract-review",
    label: "Contract Review",
    icon: FileSearch,
    match: (path) => path.startsWith("/contract-review"),
    testid: "staff-nav-contract",
    blurb: "AI red-line a client contract against the original proposal",
  },
  {
    to: "/announcements",
    label: "Announcements",
    icon: Megaphone,
    match: (path) => path.startsWith("/announcements"),
    testid: "staff-nav-announcements",
    blurb: "A note clients must read on the proposal loading screen - admins only",
    adminOnly: true,
  },
  {
    to: "/team",
    label: "Team",
    icon: Users,
    match: (path) => path.startsWith("/team"),
    testid: "staff-nav-team",
    blurb: "Provision staff accounts, reset passwords and authenticators",
    adminOnly: true,
  },
  {
    to: "/board",
    label: "Board",
    icon: Landmark,
    match: (path) => path.startsWith("/board"),
    testid: "staff-nav-board",
    blurb: "Quarterly and full-year board packs - admins only",
    adminOnly: true,
  },
];

export { NAV_ITEMS };

/**
 * Persistent staff top-nav strip.
 * Renders a sticky wordmark + nav items (admin-only items filtered by
 * role) + the user menu. Active item carries the navy underline rule.
 */
export function StaffTopNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
  return (
    <header
      data-testid="staff-topnav"
      className="sticky top-0 z-40 bg-stone-50/85 backdrop-blur-md border-b border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-8">
        {/* Wordmark - quiet, doesn't compete with the nav. */}
        <Link
          to="/share"
          data-testid="staff-wordmark"
          className="group flex items-baseline gap-2 select-none"
        >
          <span className="text-[11px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] font-semibold">
            Medicys
          </span>
          <span className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-400 hidden sm:inline">
            · staff workspace
          </span>
        </Link>

        {/* Primary nav. */}
        <nav className="flex items-center gap-1 flex-1" aria-label="Staff sections">
          {navItems.map(({ to, label, icon: Icon, match, testid }) => {
            const active = match(pathname);
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={[
                  "relative inline-flex items-center gap-2 px-3 py-2 rounded-md",
                  "text-[13px] font-medium transition-colors",
                  active
                    ? "text-[#1B4F8A]"
                    : "text-slate-600 hover:text-slate-900 hover:bg-stone-100",
                ].join(" ")}
              >
                <Icon
                  className={`w-4 h-4 ${active ? "text-[#3EA8DC]" : "text-stone-400"}`}
                  strokeWidth={2}
                />
                <span>{label}</span>
                {active && (
                  <span className="absolute -bottom-[13px] left-3 right-3 h-[2px] bg-[#1B4F8A] rounded-full" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Quiet right-rail: user menu (initials avatar) + admin
            shortcut for adding staff + sign-out. */}
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * Compact user menu shown on the far right of the staff nav.
 * - Renders the signed-in user's initials inside a circle.
 * - Click to open a popover with full name, role badge, "Add staff"
 *   shortcut (admins only) and "Sign out".
 *
 * Doesn't render anything when ``useAuth`` reports no user - the
 * nav is reused on the Login page too via the wordmark left side.
 */
function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials =
    (user.name || user.email || "")
      .split(/[@. ]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0].toUpperCase())
      .join("") || "?";
  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="staff-user-menu-btn"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#1B4F8A]/10 hover:bg-[#1B4F8A]/15 text-[#1B4F8A] text-[11px] font-semibold tracking-wider transition-colors"
        aria-label="Open user menu"
      >
        {initials}
      </button>
      {open && (
        <>
          {/* Click-outside catcher: positioned over the whole viewport
              behind the menu so any click elsewhere closes the popover.
              z-index lower than the menu so clicks inside still register. */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            data-testid="staff-user-menu-popover"
            className="absolute right-0 mt-2 w-60 z-50 bg-white border border-stone-200 shadow-lg rounded-xl py-1 text-sm"
          >
            <div className="px-4 py-3 border-b border-stone-100">
              <p
                className="font-medium text-slate-900 truncate"
                data-testid="staff-user-menu-name"
              >
                {user.name || user.email}
              </p>
              <p className="text-xs text-stone-500 truncate">{user.email}</p>
              <p className="mt-2 inline-block text-[10px] font-mono tracking-[0.14em] uppercase bg-stone-100 text-stone-600 px-2 py-0.5 rounded">
                {user.role || "staff"}
              </p>
            </div>
            {user.role === "admin" && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/team");
                }}
                data-testid="staff-user-menu-team"
                className="w-full flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-stone-50 transition-colors"
              >
                <UserPlus className="w-4 h-4 text-stone-400" />
                Manage team
              </button>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              data-testid="staff-user-menu-signout"
              className="w-full flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-stone-50 transition-colors"
            >
              <LogOut className="w-4 h-4 text-stone-400" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Re-import was at the bottom - moved to the top with the other React
// hook imports. This stub exists so the diff is small and a re-read of
// the file doesn't show a dangling import.

/**
 * Page wrapper for every staff page.
 * Sets the body-level light palette + max-width container, embeds
 * the persistent nav, and adds the consistent footer bar.
 *
 * Props:
 *   - children:   page contents
 *   - max:        "narrow" (3xl, single-column tools like Share /
 *                  Contract Review) or "wide" (7xl, list/grid tools
 *                  like Search / RFQs)
 *   - bodyClass:  extra classes for the <main> element
 */
export default function StaffShell({ children, max = "narrow", bodyClass = "" }) {
  const maxWidth = max === "wide" ? "max-w-7xl" : "max-w-4xl";
  return (
    <div className="min-h-screen bg-stone-50 text-slate-900 font-sans antialiased selection:bg-[#3EA8DC]/30">
      <StaffTopNav />
      <main
        className={[
          maxWidth,
          "mx-auto px-6 sm:px-8 pt-10 pb-24 flex flex-col gap-10",
          bodyClass,
        ].join(" ")}
      >
        {children}
      </main>
      <footer className="border-t border-stone-200 bg-white/40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between text-[11px] font-mono text-stone-500 tracking-wide">
          <span>medicys · confidential · staff only</span>
          <span className="hidden sm:inline">
            healthcare fieldwork experts
          </span>
        </div>
      </footer>
    </div>
  );
}
